import Markdoc, { type Node, type RenderableTreeNode } from "@markdoc/markdoc";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@mountayaapp/locales";
import { type Frontmatter, parseDocument, type TocEntry } from "@mountayaapp/markdoc";

import { buildDocHref } from "./href";
import {
  type NavGroup as CuratedNavGroup,
  NAVIGATION,
  resolveTabId,
  TABS,
  type Tab,
  type TabId,
} from "./navigation";

export type { Tab, TabId };
// Re-export the tab information architecture from the registry so the header,
// shell, and App import the tab config and types from the same content surface
// they already read the manifest from, rather than reaching into the navigation
// module directly. The navigation module stays the place the tabs are defined.
export { TABS };

/**
 * @mountayaapp/docs content registry. Authored Markdoc pages live under
 * `content/<lang>/**` and are pulled in at build time with `import.meta.glob`,
 * parsed once, and indexed by language and slug. The module is render-free (no
 * JSX) so both the client entry and the prerender step import it; the JSX
 * renderers consume the parsed `tree` separately. The sidebar order is the
 * curated tree in `./navigation`, not the pages' frontmatter — frontmatter owns
 * only each page's `title` and `description`.
 */

/**
 * DocEntry is one authored page: its language, its trailing-slash-free slug
 * (empty for a language root), the validated frontmatter, the renderable tree,
 * the table of contents, and the canonical href the page is served at.
 */
export interface DocEntry {
  frontmatter: Frontmatter;
  href: string;
  lang: SupportedLanguage;
  slug: string;
  toc: TocEntry[];
  tree: RenderableTreeNode;
}

/**
 * NavItem is one link in the sidebar navigation: a page's localized title and
 * the href it points to.
 */
export interface NavItem {
  href: string;
  title: string;
}

/**
 * NavGroup is one resolved section of the sidebar navigation: the owning `tab`,
 * the localized `labelKey` and Nucleo `icon` from the curated tree, plus the
 * `items` resolved from that group's slugs for the active language. This module is
 * render-free, so it carries the `labelKey` and the `icon` component rather than a
 * rendered heading; the consumer translates the key and draws the icon. The `tab`
 * tag lets `getNavManifest` hand a sidebar only the groups its active tab owns.
 */
export interface NavGroup {
  icon: CuratedNavGroup["icon"];
  items: NavItem[];
  labelKey: string;
  tab: TabId;
}

interface RawDoc {
  frontmatter: Frontmatter;
  lang: SupportedLanguage;
  slug: string;
  toc: TocEntry[];
  tree: RenderableTreeNode;
}

/**
 * isSupportedLanguage narrows a path-derived language segment to a supported
 * language, so content under an unrecognized directory is ignored rather than
 * indexed under an invalid key.
 */
const isSupportedLanguage = (value: string): value is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/**
 * deriveLocation turns a globbed file path into its `(lang, slug)`. It strips
 * everything up to and including the `content/` segment and the `.md` suffix;
 * the first remaining segment is the language and the rest is the slug. A
 * trailing `index` segment collapses to its parent path, so each section is a
 * folder with an `index.md` landing: `map-embedding/index.md` is the slug
 * `map-embedding` and a top-level `index.md` is the empty slug (the language
 * root). A flat file keeps its path as the slug.
 */
export const deriveLocation = (path: string): { lang: string; slug: string } => {
  const afterContent = path.replace(/^.*\/content\//, "").replace(/\.md$/, "");
  const [lang = "", ...rest] = afterContent.split("/");
  const joined = rest.join("/");
  const slug = joined === "index" ? "" : joined.replace(/\/index$/, "");

  return { lang, slug };
};

/**
 * partialModules are the raw source of every shared partial under
 * `content/_partials/**`. Pages reference them with `{% partial file="…" %}`,
 * where the `file` value is the partial's path relative to the `content/`
 * directory — for example `_partials/activities.md`. Keeping the enum and
 * parameter tables in partials lets a page include them without duplicating the
 * markup, and a single `file=` convention means the same string identifies a
 * partial whether it is included from a top-level page or a nested section.
 */
const partialModules = import.meta.glob<string>("../../content/_partials/**/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
});

/**
 * partials maps each `{% partial file="…" %}` key to its parsed Markdoc AST. The
 * key is the partial's path relative to `content/` (e.g. `_partials/activities.md`),
 * matching the `file` attribute authors write. `parseDocument` merges this map
 * into the transform config so a page's partial reference expands to that AST at
 * parse time.
 */
const partials: Record<string, Node> = {};
for (const [path, source] of Object.entries(partialModules)) {
  const key = path.replace(/^.*\/content\//, "");
  partials[key] = Markdoc.parse(source);
}

const rawModules = import.meta.glob<string>("../../content/**/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
});

/**
 * rawDocs parses every authored page once into its frontmatter, tree, and ToC,
 * dropping any file under an unsupported language directory and any partial
 * (partials are includes, not pages, and are merged into the parse config rather
 * than indexed as routes). Each page is parsed with the shared `partials` so a
 * `{% partial file="…" %}` reference expands inline.
 */
const rawDocs: RawDoc[] = Object.entries(rawModules).flatMap(([path, source]) => {
  const { lang, slug } = deriveLocation(path);
  if (!isSupportedLanguage(lang)) {
    return [];
  }

  try {
    const { frontmatter, toc, tree } = parseDocument(source, { partials });

    return [{ frontmatter, lang, slug, toc, tree }];
  } catch (error) {
    // A single unparseable page (typically malformed YAML frontmatter) must not
    // take down the whole site, so drop it with a dev warning instead of throwing
    // through this module's top-level evaluation.
    if (import.meta.env.DEV) {
      console.warn(`@mountayaapp/docs: skipping unparseable page ${path}`, error);
    }

    return [];
  }
});

/**
 * docsByLanguage indexes parsed pages by language then slug for O(1) lookup by
 * the resolver, attaching each page's canonical href.
 */
const docsByLanguage = new Map<SupportedLanguage, Map<string, DocEntry>>();
for (const raw of rawDocs) {
  const bySlug = docsByLanguage.get(raw.lang) ?? new Map<string, DocEntry>();
  bySlug.set(raw.slug, { ...raw, href: buildDocHref(raw.lang, raw.slug) });
  docsByLanguage.set(raw.lang, bySlug);
}

/**
 * isReachableSlug answers whether a slug is reachable from the chrome: either a
 * sidebar group lists it, or a non-fallback header tab owns it. The three
 * full-width explorers — the GraphQL playground and the two generated references —
 * are reached through their own tab rather than a rail entry, so the tab clause is
 * what keeps them out of the orphan warning below.
 */
const curatedSlugs = new Set<string>(NAVIGATION.flatMap((group) => group.slugs));
const isReachableSlug = (slug: string): boolean =>
  curatedSlugs.has(slug) || resolveTabId(slug).id !== "documentation";

/**
 * findDoc looks up a parsed page by language and slug, returning `null` when no
 * page is registered at that location.
 */
export const findDoc = (lang: SupportedLanguage, slug: string): DocEntry | null =>
  docsByLanguage.get(lang)?.get(slug) ?? null;

/**
 * resolveManifest turns the curated navigation tree into the sidebar for one
 * language. Each group keeps its `labelKey` and `icon`; each slug resolves to a
 * page in the requested language, falling back to the default language so a
 * not-yet-translated page still links to its English version. A slug with no
 * page in either language is omitted, so the rendered sidebar never carries a
 * dead link while content is still being authored.
 */
const resolveManifest = (lang: SupportedLanguage): NavGroup[] =>
  NAVIGATION.map(({ icon, labelKey, slugs, tab }) => {
    const items: NavItem[] = [];
    for (const slug of slugs) {
      const entry = findDoc(lang, slug) ?? findDoc(DEFAULT_LANGUAGE, slug);
      if (entry) {
        items.push({ href: buildDocHref(lang, slug), title: entry.frontmatter.title });
      }
    }

    return { icon, items, labelKey, tab };
  });

const manifestByLanguage = new Map<SupportedLanguage, NavGroup[]>();
for (const lang of docsByLanguage.keys()) {
  manifestByLanguage.set(lang, resolveManifest(lang));
}

/**
 * warnNavigationGaps reports two kinds of authoring drift, only while developing
 * (so a production build is never noisy): a curated slug that resolves to no
 * page in any language (a sidebar entry that would be dead, and is omitted), and
 * an authored page that no group references (an orphan unreachable from the
 * sidebar). Both are warnings, never throws, so the app builds before every
 * page in the tree exists.
 *
 * A group whose slugs ALL resolve to nothing is a section declared ahead of its
 * content rather than drift, so it is skipped: the chrome already omits an empty
 * group and hides its tab. A group where only SOME slugs resolve is real drift and
 * still warns.
 */
const warnNavigationGaps = (): void => {
  if (!import.meta.env.DEV) {
    return;
  }

  const resolvableSlugs = new Set<string>();
  for (const bySlug of docsByLanguage.values()) {
    for (const slug of bySlug.keys()) {
      resolvableSlugs.add(slug);
    }
  }

  for (const group of NAVIGATION) {
    if (!group.slugs.some((slug) => resolvableSlugs.has(slug))) {
      continue;
    }

    for (const slug of group.slugs) {
      if (!resolvableSlugs.has(slug)) {
        console.warn(
          `[docs] navigation references "${slug}" (group "${group.labelKey}") but no page resolves it; the sidebar entry is omitted.`,
        );
      }
    }
  }

  for (const slug of resolvableSlugs) {
    if (!isReachableSlug(slug)) {
      console.warn(
        `[docs] page "${slug}" is authored but no sidebar group lists it and no header tab owns it; it is unreachable from the chrome.`,
      );
    }
  }
};

warnNavigationGaps();

/**
 * getNavManifest returns the curated sidebar navigation for a language, optionally
 * narrowed to a single tab. With no `tabId` it returns every group (used by the
 * breadcrumbs, the pager, and the discovery files, which resolve a page's owning
 * section across the whole tree); with a `tabId` it returns only that tab's groups
 * (used by the sidebar, which shows the active tab alone). Groups that resolved to
 * no items are dropped, so a section declared ahead of its content never renders as
 * an empty heading. A language with no content yet yields an empty list.
 */
export const getNavManifest = (lang: SupportedLanguage, tabId?: TabId): NavGroup[] => {
  const groups = (manifestByLanguage.get(lang) ?? []).filter((group) => group.items.length > 0);

  return tabId ? groups.filter((group) => group.tab === tabId) : groups;
};

/**
 * listVisibleTabs returns the header tabs that have somewhere to go in a language:
 * a tab renders only when its landing page resolves, falling back to the default
 * language the way the manifest does. The tab IS a link to that page, so a tab
 * whose landing slug has no page would be a dead link. This is the single seam a
 * section declared ahead of its content passes through, so authoring its pages is
 * all it takes to turn the tab on.
 */
export const listVisibleTabs = (lang: SupportedLanguage): Tab[] =>
  TABS.filter(
    (tab) =>
      findDoc(lang, tab.landingSlug) !== null ||
      findDoc(DEFAULT_LANGUAGE, tab.landingSlug) !== null,
  );

/**
 * resolveTab maps a URL pathname to the header tab that owns it. The first path
 * segment is the language prefix and the rest is the slug; that slug runs through
 * `resolveTabId`, so the same ownership rule drives the header's active-state, the
 * shell's layout choice, and the sidebar's tab filter from one place. A pathname
 * with no slug (a language root) resolves to the Documentation fallback.
 */
export const resolveTab = (pathname: string): Tab => {
  const segments = pathname.split("/").filter(Boolean);
  const slug = segments.slice(1).join("/");

  return resolveTabId(slug);
};

/**
 * listLanguages returns the languages that have at least one authored page, in
 * the order the supported-language list declares them.
 */
export const listLanguages = (): SupportedLanguage[] =>
  SUPPORTED_LANGUAGES.filter((lang) => docsByLanguage.has(lang));

/**
 * listEntries returns every parsed page across all languages, used by the
 * prerender step to emit one static file per page.
 */
export const listEntries = (): DocEntry[] =>
  [...docsByLanguage.values()].flatMap((bySlug) => [...bySlug.values()]);
